import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import LocationIcon from '@/components/icons/Location';
import LocationSearch from '@/components/input/LocationSearch';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import SocialMetaTags from '@/components/SocialMetaTags';
import Spinner from '@/components/Spinner';
import { COOKIE_MAX_AGE_1YEAR, CURRENT_LOCATION_KEY, FeedTabs, HOME_PAGE_TITLE, IS_CREDIT_USER, LAT_LNG_COOKIE_NAME, MANUAL_LOCATION_ENTRY_KEY, RECENT_SEARCHES_KEY, REDIRECT_URI_KEY } from '@/constants/common';
import { RECENT_SEARCHES_COUNT } from '@/constants/location';
import { useAppDispatch } from '@/hooks/reduxHooks';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import CurrentLocation, { GeoLocationParams } from '@/models/Location';
import { fetchHyperlocalData, setCurrentLocation } from '@/store/features/locationSlice';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'
import { getCurrentLocationValues, getGeoLocationValues, getLocationDetails } from '@/utils/google';
import { getCookieValue, getLocalStorageKey, setLocalStorageKey } from '@/utils/helper';
interface Props {
  title: string;
}
const Location = ({
  title
}: Props) => {
  const {
    t
  } = useTranslation('location');
  const {
    locale,
    query,
    replace,
    back
  } = useRouter();
  const dispatch = useAppDispatch();
  const [locationPermissionLoading, setLocationPermissionLoading] = useState(false);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [loadingNextPage, setLoadNextPage] = useState(false);
  const [processingLocationUpdate, setLocationUpdateProcessing] = useState(false);
  const [showEnablePermission, setShowEnablePermission] = useState(false);
  const [recentSearches, setRecentSearches] = useState<CurrentLocation[]>([]);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  // User chose to enter the location manually, so never ask for GPS permission on their behalf
  const isManualEntry = query[MANUAL_LOCATION_ENTRY_KEY] === '1';
  const detectAutoLocationPermissions = useCallback(async () => {
    if ('permissions' in navigator) {
      const {
        state
      } = await navigator.permissions.query({
        name: 'geolocation'
      });
      return {
        granted: state === 'granted',
        prompting: state === 'prompt',
        isSupported: true
      };
    }
    return {
      granted: false,
      prompting: false,
      isSupported: false
    };
  }, []);
  const saveLocation = useCallback(async (currentLocation: CurrentLocation) => {
    setLocationUpdateProcessing(true);
    dispatch(setCurrentLocation(currentLocation));
    setLocalStorageKey(CURRENT_LOCATION_KEY, currentLocation);
    document.cookie = `${LAT_LNG_COOKIE_NAME}=${currentLocation.latLng}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
    const forCreditOrder = getLocalStorageKey(IS_CREDIT_USER) == '1';
    dispatch(fetchHyperlocalData({
      latLng: currentLocation.latLng,
      language: locale || DEFAULT_LANG,
      forCreditOrder
    })).unwrap() // unwrap helps in getting the latest result of the promise
    .then(res => {
      const vendors = res.dehaatVendors;
      if (query && query[REDIRECT_URI_KEY]) {
        const redirectUrl = query[REDIRECT_URI_KEY] as string;
        trackCustomEvent(CustomEventTypes.MANDI_PRICE_LOCATION_SAVED, JSON.stringify({
          Location: currentLocation.formattedAddress
        }));
        // This prevents redirection to external domains, ensuring security
        redirectUrl.startsWith('/') && !redirectUrl.includes('://') ? replace(`${query[REDIRECT_URI_KEY]}`) : replace('/');
      } else {
        trackCustomEvent(CustomEventTypes.VENDOR_LOCATION_SELECTED, JSON.stringify({
          Location: currentLocation.formattedAddress
        }));
        let targetRoute = '/';
        if (vendors.length) {
          targetRoute = vendors.length > 1 ? '/dcs' : `/?tab=${FeedTabs.SHOP}`;
        }
        replace(targetRoute);
      }
    }).catch(() => console.error('Unable to fetch location')).finally(() => setLocationUpdateProcessing(false));
  }, [dispatch, locale, query, replace]);
  const promptLocationPermission = useCallback(async (changeLocation: boolean, initialLocationSearchFocus = false) => {
    setLoadNextPage(changeLocation);
    setLocationPermissionLoading(true);
    const location = await getCurrentLocationValues();
    if (location) {
      const {
        latitude,
        longitude
      } = location;
      if (changeLocation) {
        trackCustomEvent(CustomEventTypes.LOCATION_PERMISSION_GRANTED);
        saveLocation(await getGeoLocationValues(latitude, longitude));
      }
      setLocationPermissionGranted(true);
    } else {
      // Focus the location search input and open the virtual keyboard for mobile devices
      // NOTE: this only works when the user has interacted with the website before (e.g. click) due to browser policy
      if (initialLocationSearchFocus && locationInputRef.current) {
        locationInputRef.current.focus();
      }
      setLocationPermissionGranted(false);
    }
    setLocationPermissionLoading(false);
    setLoadNextPage(false);
  }, [saveLocation]);
  const updateLocation = useCallback(({
    addressComponents,
    ...rest
  }: GeoLocationParams) => {
    setLoadNextPage(true);
    const locationDetails = getLocationDetails(addressComponents);
    const selectedLocation: CurrentLocation = {
      ...locationDetails,
      ...rest
    };
    setRecentSearches(prevRecentSearches => {
      const index = prevRecentSearches.findIndex(item => item.formattedAddress === selectedLocation.formattedAddress);
      const restOfArray = index === -1 ? prevRecentSearches.slice(0, RECENT_SEARCHES_COUNT - 1) : prevRecentSearches.filter((_, recentIndex) => recentIndex !== index);
      const newRecentSearches = [selectedLocation, ...restOfArray];
      setLocalStorageKey(RECENT_SEARCHES_KEY, newRecentSearches);
      return newRecentSearches;
    });
    saveLocation(selectedLocation);
  }, [saveLocation]);

  // Side effect which gets recent location search history from local storage
  useEffect(() => {
    const recent = getLocalStorageKey<CurrentLocation[], null>(RECENT_SEARCHES_KEY, null);
    if (recent) {
      setRecentSearches(recent);
    }
  }, []);

  // Prompt user who comes to the page for the first time
  useEffect(() => {
    const promptUserForLocationPermission = async () => {
      const {
        granted,
        prompting,
        isSupported
      } = await detectAutoLocationPermissions();
      if (granted) {
        setLocationPermissionGranted(granted);
      } else if (isManualEntry) {
        locationInputRef.current?.focus();
      } else if (prompting || !isSupported) {
        const latLng = getCookieValue(LAT_LNG_COOKIE_NAME);
        promptLocationPermission(latLng == null, true);
      }
    };
    promptUserForLocationPermission();
  }, [detectAutoLocationPermissions, isManualEntry, promptLocationPermission]);
  const locationAccessDenied = !(locationPermissionGranted || locationPermissionLoading);
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="location.tsx">
        <meta name="title" content={title} data-sentry-element="meta" data-sentry-source-file="location.tsx" />
        <meta name="description" content={t('common:app_description')} data-sentry-element="meta" data-sentry-source-file="location.tsx" />
      </Head>
      <SocialMetaTags title={title} description={t('common:app_description')} data-sentry-element="SocialMetaTags" data-sentry-source-file="location.tsx" />
      <main className="bg-white h-screen">
        <HeaderWithBackButton heading={t('your_current_location')} showHomeIcon={false} showProfileIcon={false} onBackClick={back} data-sentry-element="HeaderWithBackButton" data-sentry-source-file="location.tsx" />
        <section className="p-4 pt-16">
          <div className="my-6">
            <LocationSearch getLocation={updateLocation} onFocus={() => setShowEnablePermission(false)} placeholder={t('search_address')} ref={locationInputRef} disabled={locationPermissionLoading || loadingNextPage} data-sentry-element="LocationSearch" data-sentry-source-file="location.tsx" />
          </div>
          {isManualEntry && locationAccessDenied ? null : <div className={`flex cursor-pointer mb-6 border p-6 border-dashed rounded-xl ${locationAccessDenied ? 'border-error-90 bg-error-10' : 'border-primary-100 bg-primary-10'}`} onClick={() => {
          promptLocationPermission(true);
          setShowEnablePermission(true);
        }} data-testid="location-icon-div">
              <LocationIcon className={`w-8 h-8 fill-primary-100 ${locationAccessDenied ? 'fill-error-90' : 'fill-primary-100'}`} />
              <div className="ml-3 font-medium">
                <Paragraph variant={VARIANT.BIG} className={locationAccessDenied ? 'text-error-90 text-xl' : 'text-primary-100'}>
                  {locationAccessDenied ? t('access_denied') : t('current_location')}
                </Paragraph>
                <Paragraph variant={VARIANT.MEDIUM} className={locationAccessDenied ? 'font-semibold text-neutral-80 text-lg' : 'font-semibold text-neutral-80'}>
                  {locationAccessDenied ? t('access_denied_subtext') : t('using_gps')}
                </Paragraph>
              </div>
            </div>}
          {recentSearches.length > 0 ? <div data-testid="location-recentSearches-div">
              <Paragraph variant={VARIANT.BIG} className="font-medium">
                {t('recent_searches')}
              </Paragraph>
              {recentSearches.map((location, index) => <div key={location.formattedAddress} onClick={() => saveLocation(location)} data-testid={`location-recentSearches-div-${index}`}>
                  <Paragraph variant={VARIANT.MEDIUM} className="my-2 font-medium text-neutral-50 cursor-pointer">
                    {location.formattedAddress}
                  </Paragraph>
                  <hr />
                </div>)}
            </div> : null}
        </section>
        {!locationPermissionLoading && !locationPermissionGranted && showEnablePermission && <div className="bottom-10 fixed px-4 w-full">
              <Paragraph className="bg-error-10 mx-auto px-4 py-2 border border-error-90 rounded-xl w-fit text-error-90 text-center" variant={VARIANT.MEDIUM}>
                {t('enable_location_permission')}
              </Paragraph>
            </div>}
      </main>
      {processingLocationUpdate || locationPermissionLoading || loadingNextPage ? <Spinner /> : null}
    </>;
};
export async function getServerSideProps({
  req: {
    cookies
  }
}: GetServerSidePropsContext) {
  const locationCookie = cookies[LAT_LNG_COOKIE_NAME];
  return {
    props: {
      title: locationCookie == null ? HOME_PAGE_TITLE : 'Set your Location'
    }
  };
}
export default Location;